import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { Send, Trash2, MessagesSquare, Maximize2, ArrowLeft, Heart } from 'lucide-react';
import liveChatService, {
  LiveChatEntitySegment,
  LiveChatMessage,
  LiveChatLikeEvent,
} from '../../services/liveChatService';
import socketService from '../../services/socketService';
import commentService from '../../services/commentService';
import { useAuth } from '../../contexts/AuthContext';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import ProfileLink from '../common/ProfileLink';
import { t } from '../../i18n';
import { appAlert } from '../alerts/appAlert';
import EmojiPickerButton from '../emoji/EmojiPickerButton';
import { useEmojiInsert } from '../../hooks/useEmojiInsert';
import { isTouchDevice, shouldSendOnEnter } from '../../utils/emojiCatalog';

// ===================================================================
// LiveChat — generischer Gruppen-Live-Chat (2026-08-27).
// Wiederverwendbar für jeden Bereich: einfach
//   <LiveChat entityType="prediction-game" entityId={id} />
// einbauen (entityType = URL-Segment des Backends).
// Realtime über Socket.io + sanftes Polling als Fallback.
// ===================================================================

const TEAL = '#3fced1';
const POLL_INTERVAL = 30000; // Fallback, falls Socket-Events verloren gehen

interface Props {
  entityType: LiveChatEntitySegment;
  entityId?: string | null;
  /** Höhe der Nachrichtenliste (Standard: 480px); Strings wie '60vh' erlaubt */
  height?: number | string;
  /** Eigener Leerzustand-Text (Standard: "Noch keine Nachrichten — schreib die erste!") */
  emptyText?: string;
  /** true: auf Mobile (<640px) beim Einblenden direkt im Vollbild öffnen */
  autoFullscreenOnMobile?: boolean;
  /** true: startet direkt im Vollbild (Overlay-Modus) */
  initialFullscreen?: boolean;
  /** Overlay-Modus: Back schließt das Overlay über diesen Callback (statt in die Karten-Ansicht zu wechseln) */
  onFullscreenClose?: () => void;
  /**
   * Full-Bleed-Modus (Lobby-Chat auf der Startseite):
   * - `height` gilt für die GANZE Komponente (Wurzel = flex flex-col),
   *   Nachrichtenliste = flex-1, Composer als nicht-scrollender Footer
   *   (shrink-0) → Eingabefeld ist IMMER unten sichtbar
   * - auf Mobile (<640px) ohne Card-Optik (kein rounded/border/bg),
   *   ab sm: wie bisher als Card
   * Default false = exakt bisheriges Verhalten (Tippspiel-Seite unverändert:
   * `height` = Höhe der Nachrichtenliste, Card-Optik auf allen Breakpoints).
   */
  fullBleed?: boolean;
}

const formatTime = (ts?: number | null): string => {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
};

const formatDay = (ts: number): string => {
  const date = new Date(ts);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return t('web.component.home-live-pdc-lead.heute');
  if (date.toDateString() === yesterday.toDateString()) return t('web.component.home-new-tournaments.gestern');
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const LiveChat: React.FC<Props> = ({ entityType, entityId, height = 480, emptyText, autoFullscreenOnMobile = false, initialFullscreen = false, onFullscreenClose, fullBleed = false }) => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<LiveChatMessage[]>([]);
  // Nachrichten-IDs mit laufendem Like-Request (kein Doppel-Toggle)
  const likeBusyRef = useRef<Set<string>>(new Set());
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [input, setInput] = useState('');
  const { fieldRef: inputFieldRef, insert: insertEmojiIntoInput } = useEmojiInsert<HTMLTextAreaElement>(input, setInput);
  const [sending, setSending] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(initialFullscreen);
  const listRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);

  const viewerId = user?.id ?? null;
  const isAdmin = useIsAdmin();

  // Neue Nachrichten dedupliziert einsortieren (Socket + Polling können sich überlappen)
  const mergeMessages = useCallback((incoming: LiveChatMessage[]) => {
    setMessages((prev) => {
      const known = new Set(prev.map((m) => m.id));
      const fresh = incoming.filter((m) => m.id && !known.has(m.id));
      // Bekannte Nachrichten: Like-Zähler/-Status aus dem Polling übernehmen
      // (außer während eines laufenden eigenen Toggles)
      const byId = new Map(incoming.filter((m) => m.id).map((m) => [m.id as string, m]));
      let changed = false;
      const updated = prev.map((m) => {
        const inc = m.id ? byId.get(m.id) : undefined;
        if (!inc || likeBusyRef.current.has(m.id as string)) return m;
        const likeCount = inc.likeCount ?? 0;
        const likedByMe = inc.likedByMe === true;
        if ((m.likeCount ?? 0) === likeCount && (m.likedByMe === true) === likedByMe) return m;
        changed = true;
        return { ...m, likeCount, likedByMe };
      });
      if (fresh.length === 0) return changed ? updated : prev;
      return [...updated, ...fresh].sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0));
    });
  }, []);

  // Like-Zähler/-Status einer Nachricht lokal setzen (pure Update-Helfer)
  const applyLike = useCallback((messageId: string, patch: { likeCount?: number; likedByMe?: boolean }) => {
    setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, ...patch } : m)));
  }, []);

  const loadLatest = useCallback(async (initial: boolean) => {
    if (!entityId) return;
    try {
      const result = await liveChatService.getMessages(entityType, entityId, { limit: 50 });
      if (initial) {
        setMessages(result.messages);
        setHasMore(result.hasMore);
      } else {
        mergeMessages(result.messages);
      }
    } catch {
      /* Polling-Fehler still schlucken — Socket bleibt aktiv */
    } finally {
      if (initial) setLoading(false);
    }
  }, [entityType, entityId, mergeMessages]);

  // Initial-Load + Polling-Fallback
  useEffect(() => {
    if (!entityId) return;
    setLoading(true);
    loadLatest(true);
    const interval = window.setInterval(() => loadLatest(false), POLL_INTERVAL);
    return () => window.clearInterval(interval);
  }, [entityId, loadLatest]);

  // Realtime: Raum-Channel subscriben
  useEffect(() => {
    if (!entityId) return;
    const channel = liveChatService.buildChannelName(entityType, entityId);
    socketService.subscribe(channel);

    const offMessage = socketService.on<LiveChatMessage>('liveChatMessage', (msg) => {
      if (msg?.entityId === entityId) mergeMessages([msg]);
    });
    const offDeleted = socketService.on<{ id?: string; entityId?: string }>(
      'liveChatMessageDeleted',
      (payload) => {
        if (payload?.entityId === entityId && payload.id) {
          setMessages((prev) => prev.filter((m) => m.id !== payload.id));
        }
      },
    );

    // Likes live: Zähler für alle, likedByMe nur wenn ich selbst der Auslöser bin
    const offLiked = socketService.on<LiveChatLikeEvent>('liveChatMessageLiked', (payload) => {
      if (payload?.entityId !== entityId || !payload.id) return;
      const patch: { likeCount?: number; likedByMe?: boolean } = {};
      if (typeof payload.likeCount === 'number') patch.likeCount = Math.max(0, payload.likeCount);
      if (viewerId != null && payload.userId === viewerId && typeof payload.liked === 'boolean') {
        patch.likedByMe = payload.liked;
      }
      applyLike(payload.id, patch);
    });

    return () => {
      offMessage();
      offDeleted();
      offLiked();
      socketService.unsubscribe(channel);
    };
  }, [entityType, entityId, mergeMessages, applyLike, viewerId]);

  // Auf Mobile direkt im Vollbild starten (Tab-Klick = Chat öffnen)
  useEffect(() => {
    if (autoFullscreenOnMobile && window.innerWidth < 640) {
      setIsFullscreen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Im Fullscreen den Seiten-Scroll sperren
  useEffect(() => {
    if (!isFullscreen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isFullscreen]);

  // Auto-Scroll ans Ende, solange der Nutzer unten "klebt"
  useEffect(() => {
    const list = listRef.current;
    if (list && stickToBottomRef.current) {
      list.scrollTop = list.scrollHeight;
    }
  }, [messages, loading]);

  const handleScroll = () => {
    const list = listRef.current;
    if (!list) return;
    stickToBottomRef.current = list.scrollHeight - list.scrollTop - list.clientHeight < 80;
  };

  const loadOlder = async () => {
    if (!entityId || loadingOlder) return;
    const oldest = messages[0]?.createdAt;
    if (!oldest) return;
    setLoadingOlder(true);
    try {
      const result = await liveChatService.getMessages(entityType, entityId, {
        limit: 50,
        before: oldest,
      });
      setHasMore(result.hasMore);
      setMessages((prev) => {
        const known = new Set(prev.map((m) => m.id));
        const older = result.messages.filter((m) => m.id && !known.has(m.id));
        return [...older, ...prev];
      });
    } catch {
      /* ignorieren */
    } finally {
      setLoadingOlder(false);
    }
  };

  const handleSend = async () => {
    const content = input.trim();
    if (!content || !entityId || sending) return;
    setSending(true);
    try {
      const msg = await liveChatService.postMessage(entityType, entityId, content);
      setInput('');
      if (msg) mergeMessages([msg]);
      stickToBottomRef.current = true;
    } catch (e: any) {
      appAlert.error(e, undefined, t('web.component.live-chat.nachricht-konnte-nicht-gesendet-werden'));
    } finally {
      setSending(false);
    }
  };

  // Like togglen: optimistisch, bei Fehler zurückrollen; Gäste → Login
  const handleToggleLike = async (msg: LiveChatMessage) => {
    const messageId = msg.id;
    if (!messageId) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (likeBusyRef.current.has(messageId)) return;
    likeBusyRef.current.add(messageId);
    const prevLiked = msg.likedByMe === true;
    const prevCount = msg.likeCount ?? 0;
    applyLike(messageId, {
      likedByMe: !prevLiked,
      likeCount: Math.max(0, prevCount + (prevLiked ? -1 : 1)),
    });
    try {
      const result = await liveChatService.toggleLike(messageId);
      applyLike(messageId, { likedByMe: result.liked, likeCount: result.likeCount });
    } catch {
      applyLike(messageId, { likedByMe: prevLiked, likeCount: prevCount });
    } finally {
      likeBusyRef.current.delete(messageId);
    }
  };

  const handleDelete = async (messageId: string) => {
    try {
      await liveChatService.deleteMessage(messageId);
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
    } catch {
      /* ignorieren */
    }
  };

  // Tagestrenner vorbereiten
  const rows = useMemo(() => {
    const result: Array<{ type: 'day'; key: string; label: string } | { type: 'msg'; key: string; msg: LiveChatMessage }> = [];
    let lastDay = '';
    messages.forEach((msg) => {
      const day = msg.createdAt ? new Date(msg.createdAt).toDateString() : '';
      if (day && day !== lastDay) {
        lastDay = day;
        result.push({ type: 'day', key: `day-${day}`, label: formatDay(msg.createdAt!) });
      }
      result.push({ type: 'msg', key: msg.id ?? `msg-${result.length}`, msg });
    });
    return result;
  }, [messages]);

  // Wurzel ist IMMER flex flex-col: Kopfzeile + Composer sind shrink-0-
  // Zeilen, die Nachrichtenliste scrollt dazwischen → der Composer kann
  // nie aus dem Chat herausgeschoben werden.
  //   - Card-Modus (Default): Liste hat die feste `height` (bisheriges
  //     Verhalten, Tippspiel-Seite unverändert)
  //   - fullBleed: `height` sitzt auf der Wurzel, Liste = flex-1; auf
  //     Mobile ohne Card-Optik, ab sm: Card wie bisher
  const chatUi = (
    <div
      className={
        isFullscreen
          ? 'fixed inset-0 z-[200] flex flex-col bg-zinc-950'
          : fullBleed
            ? 'flex min-h-0 flex-col overflow-hidden sm:rounded-2xl sm:border sm:border-zinc-800 sm:bg-zinc-900/60'
            : 'flex flex-col rounded-2xl border border-zinc-800 bg-zinc-900/60 overflow-hidden'
      }
      style={!isFullscreen && fullBleed ? { height } : undefined}
    >
      {isFullscreen ? (
        /* Vollbild-Kopfzeile wie in der App: Back-Pfeil + zentrierter Titel */
        <div className="flex h-14 shrink-0 items-center border-b border-zinc-800 px-2">
          <button
            onClick={() => (onFullscreenClose ? onFullscreenClose() : setIsFullscreen(false))}
            className="p-2.5 rounded-xl text-white hover:bg-zinc-800 transition"
            aria-label={t('web.component.chats-manager.zurueck')}
          >
            <ArrowLeft size={22} />
          </button>
          <span className="flex-1 text-center text-base font-semibold text-white">{t('web.component.live-chat.live-chat')}</span>
          <div className="w-11" aria-hidden="true" />
        </div>
      ) : (
        /* Karten-Kopfzeile: Titel + Vollbild-Button (mobile) */
        <div className="flex shrink-0 items-center justify-between px-4 py-2.5 border-b border-zinc-800">
          <span className="text-sm font-semibold text-white">{t('web.component.live-chat.live-chat')}</span>
          <button
            onClick={() => setIsFullscreen(true)}
            className={`p-2 -m-1 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 transition ${fullBleed ? '' : 'sm:hidden'}`}
            aria-label={t('web.component.listing-detail-parts.vollbild')}
          >
            <Maximize2 size={18} />
          </button>
        </div>
      )}

      {/* Nachrichtenliste */}
      <div
        ref={listRef}
        onScroll={handleScroll}
        className={
          isFullscreen || fullBleed
            ? 'min-h-0 flex-1 overflow-y-auto px-4 py-4'
            : 'overflow-y-auto px-4 py-4'
        }
        style={isFullscreen || fullBleed ? undefined : { height }}
      >
        {loading ? (
          <div className="h-full flex items-center justify-center text-zinc-500 text-sm">
            {t('web.component.live-chat.chat-wird-geladen')}
          </div>
        ) : messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center gap-3 text-zinc-500">
            <MessagesSquare size={40} className="opacity-40" />
            <p className="text-sm">{emptyText ?? t('web.component.live-chat.noch-keine-nachrichten-schreib-die')}</p>
          </div>
        ) : (
          <>
            {hasMore && (
              <div className="flex justify-center mb-4">
                <button
                  onClick={loadOlder}
                  disabled={loadingOlder}
                  className="px-4 py-1.5 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-medium transition disabled:opacity-60"
                >
                  {loadingOlder ? t('web.component.live-chat.laedt') : t('web.component.live-chat.aeltere-nachrichten-laden')}
                </button>
              </div>
            )}
            <div className="space-y-3">
              {rows.map((row) => {
                if (row.type === 'day') {
                  return (
                    <div key={row.key} className="flex justify-center">
                      <span className="px-3 py-1 rounded-full bg-zinc-800 text-zinc-400 text-[11px] font-medium">
                        {row.label}
                      </span>
                    </div>
                  );
                }
                const msg = row.msg;
                const own = viewerId != null && msg.senderId === viewerId;
                const avatarUrl = commentService.buildAvatarUrl(msg.senderAvatarUrl, 100);
                return (
                  <div key={row.key} className={`group flex gap-2.5 ${own ? 'flex-row-reverse' : ''}`}>
                    <ProfileLink userId={msg.senderId} displayName={msg.senderName} className="shrink-0 rounded-full hover:no-underline">
                      <div className="shrink-0 w-8 h-8 rounded-full bg-zinc-700 overflow-hidden flex items-center justify-center">
                        {avatarUrl ? (
                          <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-[11px] font-bold text-zinc-300">
                            {(msg.senderName ?? '?').charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                    </ProfileLink>
                    <div className={`max-w-[78%] ${own ? 'text-right' : ''}`}>
                      <div
                        className={
                          'inline-block px-3.5 py-2 rounded-2xl text-left ' +
                          (own
                            ? 'bg-[#3fced1]/15 border border-[#3fced1]/30'
                            : 'bg-zinc-800 border border-zinc-700/60')
                        }
                      >
                        <div className="text-[11px] font-semibold mb-0.5" style={{ color: TEAL }}>
                          <ProfileLink userId={msg.senderId} displayName={msg.senderName}>
                            {msg.senderName ?? t('web.component.live-chat.benutzer')}
                          </ProfileLink>
                        </div>
                        <p className="text-sm text-zinc-100 whitespace-pre-wrap break-words leading-snug">
                          {msg.content}
                        </p>
                        <div className="mt-0.5 flex items-center justify-end gap-2">
                          {msg.id && (
                            <button
                              type="button"
                              onClick={() => handleToggleLike(msg)}
                              className={
                                (t('web.component.live-chat.flex-items-center-gap-1') + ' ') +
                                (msg.likedByMe
                                  ? 'text-[#3fced1]'
                                  : (msg.likeCount ?? 0) > 0
                                    ? 'text-zinc-400 hover:text-[#3fced1]'
                                    : 'text-zinc-600 hover:text-[#3fced1]')
                              }
                              aria-label={msg.likedByMe ? t('web.component.live-chat.gefaellt-mir-entfernen') : t('web.component.comment-item.gefaellt-mir')}
                              aria-pressed={msg.likedByMe === true}
                              data-testid={`like-${msg.id}`}
                            >
                              <Heart size={12} fill={msg.likedByMe ? TEAL : 'none'} />
                              {(msg.likeCount ?? 0) > 0 && (
                                <span className="font-semibold tabular-nums" data-testid={`like-count-${msg.id}`}>
                                  {msg.likeCount}
                                </span>
                              )}
                            </button>
                          )}
                          {(own || isAdmin) && msg.id && (
                            <button
                              onClick={() => handleDelete(msg.id!)}
                              className="opacity-0 group-hover:opacity-100 transition text-zinc-500 hover:text-red-400"
                              aria-label={t('web.component.live-chat.nachricht-loeschen')}
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                          <span className="text-[10px] text-zinc-500">{formatTime(msg.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Eingabe — nicht-scrollender Footer, IMMER unten sichtbar */}
      <div className="shrink-0 border-t border-zinc-800 p-3">
        {isAuthenticated ? (
          <>
            <div className="flex items-end gap-2">
              <EmojiPickerButton onPick={insertEmojiIntoInput} disabled={sending} testId="livechat-emoji-button" />
              <textarea
                ref={inputFieldRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  // Desktop: Enter sendet, Shift+Enter Absatz; Touch: Enter = Absatz (Marcel 21.10.2026)
                  if (shouldSendOnEnter(e, isTouchDevice())) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                rows={1}
                maxLength={500}
                placeholder={t('web.component.live-chat.nachricht-schreiben')}
                className="flex-1 resize-none rounded-xl bg-zinc-800 border border-zinc-700 px-3.5 py-2.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-[#3fced1]/60"
              />
              <button
                onClick={handleSend}
                disabled={sending || !input.trim()}
                className="p-3 rounded-xl bg-[#3fced1] hover:bg-[#35b5b8] text-black transition disabled:opacity-50"
                aria-label={t('web.component.contact-request-detail-modal.senden')}
              >
                <Send size={18} />
              </button>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-between gap-3 px-1">
            <p className="text-sm text-zinc-400">{t('web.component.live-chat.melde-dich-an-um-mitzuschreiben')}</p>
            <Link
              to="/login"
              className="shrink-0 px-4 py-2 rounded-xl bg-[#3fced1] hover:bg-[#35b5b8] text-black text-sm font-semibold transition"
            >
              {t('web.component.header.anmelden')}
            </Link>
          </div>
        )}
      </div>
    </div>
  );

  // Fullscreen als Portal direkt in den <body>: entkommt transformierten
  // Vorfahren, sonst liegt das "fixed"-Overlay NICHT über der Navbar
  return isFullscreen ? createPortal(chatUi, document.body) : chatUi;
};

export default LiveChat;
